import React from 'react';
import { Link } from 'react-router-dom';
import MainNavigation from '../components/common/MainNavigation';
import ScrollReveal from '../components/common/ScrollReveal';
import TeslaButton from '../components/common/TeslaButton';
import SiteFooter from '../components/common/SiteFooter';
import { CONTACT_PAGE_IMAGE } from '../constants/marketingPageImages';
import StaticPageSeo from '../seo/StaticPageSeo';

const ContactPage: React.FC = () => {
  return (
    <>
      <StaticPageSeo path="/contact" />
      {/* Main Navigation */}
      <MainNavigation variant="solid" />
      
      <div className="contact-page">
        {/* Hero Section */}
        <section className="section section--hero section--dark">
          <div className="container">
            <div className="row align-items-center min-vh-100">
              <div className="col-lg-6">
                <ScrollReveal animation="fade-in-up">
                  <h1 className="text-heading-1 mb-6 text-white">
                    Get in Touch
                  </h1>
                  <p className="text-body-large mb-8 text-gray-40">
                    Have questions about our services? Need help with your account? 
                    Want to become a partner stylist? We're here to help you every step of the way.
                  </p>
                  <div className="contact-quick-info">
                    <div className="d-flex align-items-center mb-4">
                      <div className="contact-icon mr-4">📞</div>
                      <div>
                        <div className="text-white fw-medium">Call Us</div>
                        <div className="text-gray-40">
                          <a href="tel:+17373410065" className="text-gray-40" style={{ textDecoration: 'none', color: '#C9A84C' }}>
                            737 341 0065
                          </a>
                        </div>
                      </div>
                    </div>
                    <div className="d-flex align-items-center mb-4">
                      <div className="contact-icon mr-4">✉️</div>
                      <div>
                        <div className="text-white fw-medium">Email</div>
                        <div className="text-gray-40">
                          <a href="mailto:contact@lookbel.com" className="text-gray-40" style={{ textDecoration: 'none', color: '#C9A84C' }}>
                            contact@lookbel.com
                          </a>
                        </div>
                      </div>
                    </div>
                    <div className="d-flex align-items-center">
                      <div className="contact-icon mr-4">⏰</div>
                      <div>
                        <div className="text-white fw-medium">Support Hours</div>
                        <div className="text-gray-40">Mon-Fri: 8AM-8PM EST</div>
                      </div>
                    </div>
                  </div>
                </ScrollReveal>
              </div>
              <div className="col-lg-6">
                <ScrollReveal animation="fade-in-left" delay={200}>
                  <div className="hero-image">
                    <img 
                      src={CONTACT_PAGE_IMAGE}
                      alt="Customer support team"
                      className="img-fluid rounded-lg"
                      draggable={false}
                      onContextMenu={(event) => event.preventDefault()}
                    />
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Methods Section */}
        <section className="section section--center">
          <div className="container">
            <div className="row">
              <div className="col-lg-8 mx-auto text-center mb-12">
                <ScrollReveal animation="fade-in-up">
                  <h2 className="text-heading-2 mb-6">Other Ways to Reach Us</h2>
                  <p className="text-body-large">
                    Choose the method that works best for you.
                  </p>
                </ScrollReveal>
              </div>
            </div>
            
            <div className="row">
              {contactMethods.map((method, index) => (
                <div key={index} className="col-lg-4 col-md-6 mb-8">
                  <ScrollReveal animation="fade-in-up" delay={200 * index}>
                    <div className="tesla-card text-center p-8 h-100">
                      <div className="mb-6" style={{ fontSize: '4rem' }}>
                        {method.icon}
                      </div>
                      <h3 className="text-heading-3 mb-4">{method.title}</h3>
                      <p className="text-body-medium mb-6">{method.description}</p>
                      <div className="contact-method-details">
                        {method.details.map((detail, idx) => (
                          <div key={idx} className="mb-2">
                            <strong>{detail.label}:</strong> {detail.value}
                          </div>
                        ))}
                      </div>
                      {method.action && (
                        <div className="mt-6">
                          <TeslaButton variant="outline" size="md">
                            {method.action}
                          </TeslaButton>
                        </div>
                      )}
                    </div>
                  </ScrollReveal>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="section section--light">
          <div className="container">
            <div className="row">
              <div className="col-lg-8 mx-auto text-center mb-12">
                <ScrollReveal animation="fade-in-up">
                  <h2 className="text-heading-2 mb-6">Frequently Asked Questions</h2>
                  <p className="text-body-large">
                    Quick answers to common questions.
                  </p>
                </ScrollReveal>
              </div>
            </div>
            
            <div className="row">
              <div className="col-lg-10 mx-auto">
                {faqs.map((faq, index) => (
                  <ScrollReveal key={index} animation="fade-in-up" delay={100 * index}>
                    <div className="tesla-card mb-6 p-6">
                      <h3 className="text-heading-3 mb-4 text-accent">{faq.question}</h3>
                      <p className="text-body-medium">{faq.answer}</p>
                    </div>
                  </ScrollReveal>
                ))}
                
                <ScrollReveal animation="fade-in-up">
                  <div className="text-center mt-8">
                    <p className="text-body-medium mb-4">
                      Can't find what you're looking for?
                    </p>
                    <Link to="#contact-form" style={{ textDecoration: 'none' }}>
                      <TeslaButton variant="outline" size="lg">
                        Ask Us Directly
                      </TeslaButton>
                    </Link>
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </section>

        <SiteFooter />
      </div>
    </>
  );
};

// Contact methods data
const contactMethods = [
  {
    icon: '💬',
    title: 'Live Chat',
    description: 'Get instant help from our support team during business hours.',
    details: [
      { label: 'Availability', value: 'Mon-Fri, 8AM-8PM EST' },
      { label: 'Response Time', value: 'Under 2 minutes' }
    ],
    action: 'Start Chat'
  },
  {
    icon: '📞',
    title: 'Phone Support',
    description: 'Speak directly with our customer service representatives.',
    details: [
      { label: 'Number', value: '737 341 0065' },
      { label: 'Hours', value: 'Mon-Fri, 8AM-8PM EST' }
    ],
    action: 'Call Now'
  },
  {
    icon: '📧',
    title: 'Email Support',
    description: 'Send us detailed questions and we\'ll respond within 24 hours.',
    details: [
      { label: 'Email', value: 'contact@lookbel.com' },
      { label: 'Response Time', value: 'Within 24 hours' }
    ],
    action: 'Send Email'
  }
];

// FAQ data
const faqs = [
  {
    question: 'How do I book an appointment?',
    answer: 'Simply create an account, browse available stylists in your area, select your preferred service and time slot, then confirm your booking. You\'ll receive instant confirmation and reminders.'
  },
  {
    question: 'What if I need to cancel or reschedule?',
    answer: 'You can cancel or reschedule appointments up to 24 hours before your scheduled time through the app or website. For same-day changes, please call our support line.'
  },
  {
    question: 'Are the stylists verified and insured?',
    answer: 'Yes! All stylists on our platform are licensed professionals who have passed our verification process and carry professional liability insurance.'
  },
  {
    question: 'Do you offer mobile services?',
    answer: 'Many of our stylists offer mobile services and can come to your home, office, or event location. Look for the "Mobile Service" badge when browsing stylists.'
  },
  {
    question: 'How do payments work?',
    answer: 'Payment is processed securely through the app after your service is completed. We accept all major credit cards and digital payment methods.'
  },
  {
    question: 'What if I\'m not satisfied with my service?',
    answer: 'Your satisfaction is our priority. If you\'re not happy with your service, contact us within 24 hours and we\'ll work with you to make it right.'
  }
];

export default ContactPage; 